import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import {
  TabStripContext,
  useTabStripIndicators,
  useTabStripRegistration,
} from "@src/lib/ui/tabStrip";
import { cn } from "@src/lib/utils/cn";

/**
 * SegmentedControl answers "which value?": the content stays put and only a
 * parameter changes (time scale, unit, breakdown), or it is a form field
 * (user type). It never routes; for views use Tabs. See `## Segmented
 * control` in design.md.
 *
 * Built on Radix RadioGroup: role=radiogroup / radio, one Tab stop, and the
 * arrow keys move focus and select. An inset bg-tertiary track with a white
 * pill that slides to the selected option (the Tabs pill's measuring, from
 * tabStrip).
 */

export type SegmentedControlSize = "md" | "sm";

export type SegmentedControlOption<T extends string> = {
  value: T;
  /** The visible label; with `iconOnly`, the accessible name and tooltip. */
  label: string;
  /** A leading icon, rendered at size-3.5 unless it has a size-*. */
  icon?: React.ReactNode;
  disabled?: boolean;
  /** A tooltip on hover/focus. Defaults to `label` when `iconOnly`. Not
      shown on a disabled option (a disabled button gets no pointer
      events). */
  tooltip?: string;
  dataTestId?: string;
};

export type SegmentedControlProps<T extends string> = {
  value: T;
  onValueChange: (value: T) => void;
  options: readonly SegmentedControlOption<T>[];
  /** md 32px (forms, dialogs); sm 28px (toolbars, card headers). */
  size?: SegmentedControlSize;
  /** Stretch to the container and share the width evenly. */
  fill?: boolean;
  /** Show only the icons; each label becomes the item's name and tooltip. */
  iconOnly?: boolean;
  disabled?: boolean;
  "aria-label": string;
  className?: string;
  id?: string;
  "data-testid"?: string;
};

type SizeSpec = {
  track: string;
  item: string;
  pill: string;
  label: string;
  iconOnly: string;
};

// Inset track (p-[3px]); item radius = track radius − 3 (nested radius).
const SIZES: Record<SegmentedControlSize, SizeSpec> = {
  // 32px track, 26px items; 10 = 7 + 3; 14px / 450.
  md: {
    track: "h-8 rounded-lg",
    item: "h-[26px] rounded-[7px]",
    pill: "h-[26px] rounded-[7px]",
    label: "px-2.5 text-s-v4",
    iconOnly: "w-[26px] px-0",
  },
  // 28px track, 22px items; 9 = 6 + 3; 13px / 450.
  sm: {
    track: "h-7 rounded-[9px]",
    item: "h-[22px] rounded-md",
    pill: "h-[22px] rounded-md",
    label: "px-2 text-xs-v4",
    iconOnly: "w-[22px] px-0",
  },
};

type ItemProps<T extends string> = {
  option: SegmentedControlOption<T>;
  checked: boolean;
  spec: SizeSpec;
  fill: boolean;
  iconOnly: boolean;
};

function SegmentedControlItem<T extends string>({
  option,
  checked,
  spec,
  fill,
  iconOnly,
}: ItemProps<T>): React.ReactElement {
  const { setRef } = useTabStripRegistration(checked);
  const tooltip = option.tooltip ?? (iconOnly ? option.label : undefined);

  const item = (
    <RadioGroupPrimitive.Item
      value={option.value}
      disabled={option.disabled}
      aria-label={iconOnly ? option.label : undefined}
      data-testid={option.dataTestId}
      className={cn(
        "inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap",
        spec.item,
        // Square when icon-only, unless fill stretches it.
        iconOnly ? (fill ? "px-0" : spec.iconOnly) : spec.label,
        "font-sans-v4 font-normal-v4 transition-colors duration-150",
        // aria-checked, not data-state: a Tooltip trigger overwrites
        // data-state with its own open state.
        "text-text-secondary aria-[checked=false]:enabled:hover:text-text-primary aria-checked:text-text-primary",
        "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "[&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
      )}
    >
      {option.icon}
      {iconOnly ? null : <span className="truncate">{option.label}</span>}
    </RadioGroupPrimitive.Item>
  );

  // The pill measures this wrapper: Radix Radio composes its ref inline, so a
  // ref on the item itself would re-attach on every render.
  return (
    <span
      ref={setRef}
      className={cn("relative z-[1] flex", fill && "min-w-0 flex-1")}
    >
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{item}</TooltipTrigger>
          <TooltipContent side="top" sideOffset={6}>
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : (
        item
      )}
    </span>
  );
}

function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  size = "md",
  fill = false,
  iconOnly = false,
  disabled,
  className,
  id,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
}: SegmentedControlProps<T>): React.ReactElement {
  const spec = SIZES[size];
  const { setListRef, registerTab, setHoveredTabId, pill } =
    useTabStripIndicators<HTMLDivElement>();
  const stripContext = React.useMemo(
    () => ({ registerTab, setHoveredTabId }),
    [registerTab, setHoveredTabId],
  );

  // No slide on the first paint: the pill appears under the selected option,
  // then animates on later changes.
  const [animate, setAnimate] = React.useState(false);
  React.useEffect(() => {
    if (!pill.visible || animate) return;
    const frame = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(frame);
  }, [pill.visible, animate]);

  const control = (
    <RadioGroupPrimitive.Root
      ref={setListRef}
      id={id}
      value={value}
      onValueChange={(next) => onValueChange(next as T)}
      disabled={disabled}
      aria-label={ariaLabel}
      orientation="horizontal"
      data-testid={dataTestId}
      data-size={size}
      className={cn(
        "relative isolate items-center gap-0.5 bg-bg-tertiary p-[3px]",
        spec.track,
        fill ? "flex w-full" : "inline-flex w-fit",
        className,
      )}
    >
      <div
        aria-hidden
        data-segmented-indicator="pill"
        className={cn(
          "pointer-events-none absolute top-1/2 left-0 z-0 bg-bg-primary shadow-default",
          spec.pill,
          animate &&
            "transition-[transform,width,opacity] duration-200 ease-in-out motion-reduce:transition-none",
          pill.visible ? "opacity-100" : "opacity-0",
        )}
        style={{
          width: pill.width,
          transform: `translateX(${pill.left}px) translateY(-50%)`,
        }}
      />
      {options.map((option) => (
        <SegmentedControlItem
          key={option.value}
          option={option}
          checked={option.value === value}
          spec={spec}
          fill={fill}
          iconOnly={iconOnly}
        />
      ))}
    </RadioGroupPrimitive.Root>
  );

  return (
    <TabStripContext.Provider value={stripContext}>
      <TooltipProvider>{control}</TooltipProvider>
    </TabStripContext.Provider>
  );
}

export default SegmentedControl;
